import { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthContext } from "../contexts/AuthContext";
import { OwnerAuthContext } from "../contexts/OwnerAuthContext";
import { SuperAdminAuthContext } from "../contexts/SuperAdminAuthContext";
import {
  SESSION_EXPIRED_EVENT,
  loginPathForRealm,
  resolveSessionRealm,
} from "../utils/sessionExpired";

const SESSION_TOAST = {
  user: "Your session expired. Please sign in again.",
  owner: "Votre session a expiré. Veuillez vous reconnecter.",
  admin: "Your admin session expired. Please sign in again.",
};

/**
 * Listens for global 401 session expiry (api/client.js) and redirects to the
 * correct login screen with state the login forms can show as a friendly banner.
 */
export default function SessionExpiredBridge() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useContext(AuthContext);
  const { setOwner } = useContext(OwnerAuthContext);
  const { setAdmin } = useContext(SuperAdminAuthContext);
  const handlingRef = useRef(false);

  useEffect(() => {
    const onExpired = (event) => {
      if (handlingRef.current) return;

      const from = event.detail?.from || location.pathname;
      const realm = event.detail?.realm || resolveSessionRealm(from);
      if (!realm) return;

      const loginPath = loginPathForRealm(realm);
      if (location.pathname === loginPath) return;

      handlingRef.current = true;

      if (realm === "owner") {
        setOwner(null);
      } else if (realm === "admin") {
        setAdmin(null);
      } else {
        setUser(null);
      }

      toast.info(SESSION_TOAST[realm], { duration: 6000 });

      const state =
        realm === "admin"
          ? { from: { pathname: from }, sessionExpired: true }
          : { from, redirectBack: true, sessionExpired: true };

      navigate(loginPath, { replace: true, state });

      window.setTimeout(() => {
        handlingRef.current = false;
      }, 500);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [location.pathname, navigate, setAdmin, setOwner, setUser]);

  return null;
}
